// EntityActionSheet — shared "kebab" action sheet primitive used by
// the rebuilt PlayersListPanel and AgentsListPanel (Entity Redesign
// Step 2 & 4, 2026-05-16).
//
// Why this exists
// ───────────────
// Both list panels need a per-row "more actions" popover that exposes
// the new God-Mode verbs (force-verify-email, force-logout, clear-
// lockout, unlink-telegram, reset-password, test-flag, suspend,
// activate, reassign, delete). On phones the action set is 7-10 items
// long — too many for inline buttons, and a context menu doesn't work
// on touch. So this component renders as:
//
//   • Phone: full-width bottom-sheet that slides up
//   • Desktop: popover anchored to the trigger button
//
// Both flavours share the same content + ARIA roles so screen readers
// see one "menu" with one or more grouped "menuitem"s. The actions
// themselves are passed in as an array of items so the list panel can
// gate them by status (e.g. only show "Activate" when status is not
// already active, "Confirm" props for the modal-confirm verbs).
//
// Design notes
// ───────────
// Each item carries an optional `tone`: 'default' | 'danger'. Danger
// rows are red-tinted to telegraph the destructive intent. Items that
// need a confirmation modal pass `confirm: { title, body, cta }` and
// the sheet shows a stacked modal on click. The caller's onSelect
// fires after the user clicks the confirm CTA.
//
// We deliberately don't wire to any specific API — the sheet just
// calls the item's onSelect callback. This keeps it reusable across
// both panels (players and agents have different verbs).

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';

export type ActionItem = {
  /** Stable key for React. */
  key: string;
  /** Material symbol name shown on the left of the row. */
  icon: string;
  /** Primary label — short imperative phrase ("Force logout"). */
  label: string;
  /** Optional secondary line (under the label). Useful for context. */
  hint?: string;
  /** Visual tone — default ink or danger-red. */
  tone?: 'default' | 'danger';
  /** Disable / grey out (e.g. "Already verified"). */
  disabled?: boolean;
  /** Hide the row entirely (use for status-dependent items). */
  hidden?: boolean;
  /** If set, sheet shows a confirm modal before firing onSelect. */
  confirm?: {
    title: string;
    body?: string;
    cta?: string;       // CTA label; default 'Confirm'
    ctaTone?: 'default' | 'danger';
  };
  /** Fired after confirm (if any) clears. */
  onSelect: () => void | Promise<void>;
};

export type ActionGroup = {
  /** Optional group header — small uppercase label. */
  title?: string;
  items: ActionItem[];
};

export default function EntityActionSheet({
  open,
  onClose,
  title,
  subtitle,
  groups,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  groups: ActionGroup[];
}) {
  // The confirmation modal piggybacks on top of the sheet.
  const [confirming, setConfirming] = useState<ActionItem | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirming) setConfirming(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, confirming, onClose]);

  if (!open) return null;

  async function fire(item: ActionItem) {
    setBusyKey(item.key);
    try {
      await item.onSelect();
      onClose();
    } finally {
      setBusyKey(null);
      setConfirming(null);
    }
  }

  function handleClick(item: ActionItem) {
    if (item.disabled || busyKey) return;
    if (item.confirm) {
      setConfirming(item);
    } else {
      void fire(item);
    }
  }

  // Two side-by-side modes:
  //   • Phones (< sm): full-width bottom sheet (motion-y)
  //   • Desktop: centred modal-style panel
  // We render to a portal so the sheet floats above the card grid's
  // stacking context.
  const sheet = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[80] flex items-end sm:items-start sm:items-center justify-center overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
      />
      <motion.div
        initial={{ y: 32, opacity: 0, scale: 0.98 }}
        animate={{ y: 0,  opacity: 1, scale: 1 }}
        exit={{ y: 16, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md mx-auto
                   rounded-t-2xl sm:rounded-2xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-arena-200/40 dark:border-arena-700/40">
          <div className="text-[15px] font-bold text-neutral-900 dark:text-white truncate">{title}</div>
          {subtitle && (
            <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5">{subtitle}</div>
          )}
        </div>

        {/* Body — grouped action items */}
        <div className="max-h-[70vh] overflow-y-auto">
          {groups.map((g, gi) => {
            const visible = g.items.filter((it) => !it.hidden);
            if (visible.length === 0) return null;
            return (
              <div key={gi} className="px-2 py-1.5">
                {g.title && (
                  <div className="px-2 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/55">
                    {g.title}
                  </div>
                )}
                {visible.map((it) => {
                  const danger = it.tone === 'danger';
                  const isBusy = busyKey === it.key;
                  return (
                    <button
                      key={it.key}
                      role="menuitem"
                      onClick={() => handleClick(it)}
                      disabled={it.disabled || !!busyKey}
                      className={`
                        w-full flex items-start gap-3 rounded-xl px-2.5 py-2.5 text-left
                        transition
                        ${it.disabled
                          ? 'opacity-40 cursor-not-allowed'
                          : danger
                            ? 'hover:bg-rose-50 dark:hover:bg-rose-500/15 active:bg-rose-100 dark:active:bg-rose-500/25'
                            : 'hover:bg-arena-50 dark:hover:bg-arena-700/50 active:bg-arena-100 dark:active:bg-arena-700/70'}
                      `}
                    >
                      <span className={`
                        grid place-items-center w-9 h-9 rounded-xl shrink-0
                        ${danger
                          ? 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/30'
                          : 'bg-arena-50 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/85 dark:ring-arena-600/40'}
                      `}>
                        {isBusy
                          ? <Icon name="progress_activity" size={18} className="animate-spin" />
                          : <Icon name={it.icon} size={18} filled={!danger} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block text-[13.5px] font-semibold leading-tight
                          ${danger ? 'text-rose-700 dark:text-rose-200' : 'text-neutral-900 dark:text-white'}`}>
                          {it.label}
                        </span>
                        {it.hint && (
                          <span className={`block text-[11px] mt-0.5 leading-tight
                            ${danger ? 'text-rose-600/80 dark:text-rose-200/70' : 'text-arena-600 dark:text-neon-200/65'}`}>
                            {it.hint}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer — close */}
        <div className="px-4 sm:px-5 py-3 border-t border-arena-200/40 dark:border-arena-700/40
                        bg-arena-50/40 dark:bg-arena-900/40">
          <button onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl
                       bg-white dark:bg-arena-700/60
                       ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/85 hover:bg-arena-50 dark:hover:bg-arena-700
                       py-2 text-[13px] font-semibold">
            Close
          </button>
        </div>
      </motion.div>

      {/* Confirm modal — stacked above the sheet. */}
      <AnimatePresence>
        {confirming && (
          <ConfirmModal
            item={confirming}
            busy={busyKey === confirming.key}
            onCancel={() => setConfirming(null)}
            onConfirm={() => fire(confirming)}
          />
        )}
      </AnimatePresence>
    </div>
  );

  return createPortal(sheet, document.body);
}

// Lightweight confirm-modal nested inside the sheet portal.
function ConfirmModal({
  item, busy, onCancel, onConfirm,
}: {
  item: ActionItem;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const danger = item.confirm?.ctaTone === 'danger' || item.tone === 'danger';
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.14 }}
      className="absolute inset-0 z-[90] grid place-items-center p-4"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      <motion.div
        initial={{ y: 8, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-2xl p-5 space-y-3"
      >
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0
            ${danger
              ? 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/30'
              : 'bg-arena-50 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/85 dark:ring-arena-600/40'}`}>
            <Icon name={danger ? 'warning' : 'help'} size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-neutral-900 dark:text-white">
              {item.confirm?.title || item.label}
            </div>
            {item.confirm?.body && (
              <div className="text-[12.5px] text-arena-700 dark:text-neon-200/75 mt-1 leading-snug">
                {item.confirm.body}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       text-arena-700 dark:text-neon-200/75 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
            className={`px-4 py-2 rounded-full text-[13px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-60
              ${danger
                ? 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_10px_rgba(244,63,94,0.35)]'
                : 'bg-violet-600 hover:bg-violet-500 active:bg-violet-700 shadow-[0_2px_10px_rgba(139,92,246,0.35)]'}`}>
            {busy && <Icon name="progress_activity" size={14} className="animate-spin" />}
            {item.confirm?.cta || 'Confirm'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
